<!--
 * @description 图片预览弹窗组件：支持图片切换、复制文案、下载图片
 * @author AI
 * @function 图片预览、左右切换、复制文案、下载图片
 -->
<template>
  <!-- 图片预览弹窗 -->
  <el-dialog custom-class="image-preview-dialog" :visible.sync="dialogVisible" width="90%" :show-close="false" @open="handleOpen">
    <!-- 弹窗头部：提示文字 + 图片计数 + 关闭按钮 -->
    <div slot="title" class="dialog-header">
      <span class="header-tip"> <span>*</span>长图可下滑，Ctrl/⌘ + 滚轮可缩放 </span>
      <div class="header-right">
        <span class="image-count"><span>{{ currentImageIndex + 1 }}</span> / {{ images.length }}</span>
        <i class="el-icon-close close-btn" @click="handleClose"></i>
      </div>
    </div>
    <!-- 图片展示区域：支持左右切换 -->
    <div class="image-container">
      <!-- 左侧切换按钮：第一张时置灰禁用 -->
      <div class="arrow-btn left" :class="{ disabled: images.length <= 1 || currentImageIndex <= 0 }" @click="prevImage">
        <i class="el-icon-arrow-left"></i>
      </div>
      <!-- 图片内容 -->
      <div
        ref="imageWrapper"
        class="image-wrapper"
        :class="{ 'is-zoomed': zoom > 1, 'is-dragging': isDragging }"
        @wheel="handleWheel"
        @mousedown="startDrag"
        @dblclick="toggleZoom"
      >
        <img
          :src="images[currentImageIndex]"
          :key="images[currentImageIndex]"
          :style="imageStyle"
          alt="海报图片"
          class="preview-image"
          draggable="false"
        />
      </div>
      <div class="zoom-toolbar">
        <button type="button" class="zoom-btn" :disabled="zoom <= minZoom" title="缩小" @click="zoomOut">
          <i class="el-icon-minus"></i>
        </button>
        <button type="button" class="zoom-percent" title="恢复原始显示比例" @click="resetView">{{ zoomPercent }}%</button>
        <button type="button" class="zoom-btn" :disabled="zoom >= maxZoom" title="放大" @click="zoomIn">
          <i class="el-icon-plus"></i>
        </button>
        <button type="button" class="zoom-btn" title="恢复原始显示比例" @click="resetView">
          <i class="el-icon-refresh-left"></i>
        </button>
      </div>
      <!-- 右侧切换按钮：最后一张时置灰禁用 -->
      <div class="arrow-btn right" :class="{ disabled: images.length <= 1 || currentImageIndex >= images.length - 1 }" @click="nextImage">
        <i class="el-icon-arrow-right"></i>
      </div>
    </div>
    <!-- 底部操作按钮：复制文案 + 下载图片 -->
    <div slot="footer" class="dialog-footer-btns">
      <button class="footer-btn copy-btn" @click="handleCopyText" v-if="detail.copywriting">复制文案</button>
      <button class="footer-btn download-btn" @click="handleDownload">下载图片</button>
    </div>
  </el-dialog>
</template>
<script>
export default {
  name: 'ImagePreviewDialog',
  props: {
    // 弹窗显示状态（支持 .sync 修饰符）
    visible: {
      type: Boolean,
      default: false
    },
    // 待预览的图片URL数组
    images: {
      type: Array,
      default: () => []
    },
    // 当前海报详情（用于复制文案）
    detail: {
      type: Object,
      default: () => ({})
    },
    // 初始展示的图片索引（由父组件点击的图片位置决定）
    initialIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      // 当前展示的图片索引
      currentImageIndex: 0,
      zoom: 1,
      minZoom: 0.5,
      maxZoom: 4,
      zoomStep: 1,
      isDragging: false,
      dragStart: null
    };
  },
  computed: {
    // 弹窗显示状态：双向绑定到父组件
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    zoomPercent() {
      return Math.round(this.zoom * 100);
    },
    imageStyle() {
      if (this.zoom <= 1) {
        return {
          width: 'auto',
          maxWidth: `${this.zoom * 100}%`
        };
      }
      return {
        width: `${this.zoom * 100}%`,
        maxWidth: 'none'
      };
    }
  },
  beforeDestroy() {
    this.removeDragListeners();
  },
  methods: {
    /**
     * 弹窗打开时根据父组件传入的初始索引设置当前展示的图片
     */
    handleOpen() {
      this.currentImageIndex = this.initialIndex;
      this.resetView();
    },
    /**
     * 关闭弹窗
     */
    handleClose() {
      this.dialogVisible = false;
    },
    /**
     * 切换到上一张图片
     */
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
        this.resetView();
      }
    },
    /**
     * 切换到下一张图片
     */
    nextImage() {
      if (this.currentImageIndex < this.images.length - 1) {
        this.currentImageIndex++;
        this.resetView();
      }
    },
    setZoom(nextZoom) {
      const wrapper = this.$refs.imageWrapper;
      const oldZoom = this.zoom;
      const centerX = wrapper ? wrapper.scrollLeft + wrapper.clientWidth / 2 : 0;
      const centerY = wrapper ? wrapper.scrollTop + wrapper.clientHeight / 2 : 0;

      this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, Number(nextZoom.toFixed(2))));
      if (!wrapper || this.zoom === oldZoom) return;

      this.$nextTick(() => {
        const ratio = this.zoom / oldZoom;
        wrapper.scrollLeft = centerX * ratio - wrapper.clientWidth / 2;
        wrapper.scrollTop = centerY * ratio - wrapper.clientHeight / 2;
      });
    },
    zoomIn() {
      this.setZoom(this.zoom + this.zoomStep);
    },
    zoomOut() {
      this.setZoom(this.zoom - this.zoomStep);
    },
    toggleZoom() {
      this.setZoom(this.zoom === 1 ? 2 : 1);
    },
    handleWheel(event) {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      this.setZoom(this.zoom + (event.deltaY < 0 ? this.zoomStep : -this.zoomStep));
    },
    resetView() {
      this.zoom = 1;
      this.stopDrag();
      this.$nextTick(() => {
        const wrapper = this.$refs.imageWrapper;
        if (!wrapper) return;
        wrapper.scrollLeft = 0;
        wrapper.scrollTop = 0;
      });
    },
    startDrag(event) {
      if (this.zoom <= 1 || event.button !== 0) return;
      const wrapper = this.$refs.imageWrapper;
      if (!wrapper) return;

      event.preventDefault();
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: wrapper.scrollLeft,
        scrollTop: wrapper.scrollTop
      };
      window.addEventListener('mousemove', this.handleDrag);
      window.addEventListener('mouseup', this.stopDrag);
    },
    handleDrag(event) {
      if (!this.isDragging || !this.dragStart) return;
      const wrapper = this.$refs.imageWrapper;
      if (!wrapper) return;

      wrapper.scrollLeft = this.dragStart.scrollLeft - (event.clientX - this.dragStart.x);
      wrapper.scrollTop = this.dragStart.scrollTop - (event.clientY - this.dragStart.y);
    },
    stopDrag() {
      this.isDragging = false;
      this.dragStart = null;
      this.removeDragListeners();
    },
    removeDragListeners() {
      window.removeEventListener('mousemove', this.handleDrag);
      window.removeEventListener('mouseup', this.stopDrag);
    },
    /**
     * 复制文案：触发父组件事件
     */
    handleCopyText() {
      this.$emit('copy-text', this.detail.copywriting);
    },
    /**
     * 下载当前预览的图片
     */
    handleDownload() {
      this.$tools.daochu2(this.images[this.currentImageIndex]);
    }
  }
};
</script>
<style lang="scss">
@import './imagePreviewDialog.scss';
</style>
